/**
 * Tracks recent profile viewers so that ChatSocketManager can delay
 * conversation notifications when the sender just viewed the user's profile.
 *
 * VisitorSocketManager calls addRecentViewer() when a viewed-profile event
 * arrives. ChatSocketManager calls consumeRecentViewer() before showing a
 * notification — if a match is found the entry is removed (one-shot) and
 * the caller can add a delay.
 */

interface ViewerEntry {
  viewerId: number;
  expires: number;
}

const TTL = 30_000; // 30 seconds

const entries: ViewerEntry[] = [];

/** Record that `viewerId` just viewed our profile. Auto-expires after 30s. */
export const addRecentViewer = (viewerId: number): void => {
  cleanup();
  // Avoid duplicates — refresh TTL if already present
  const idx = entries.findIndex(e => e.viewerId === viewerId);
  if (idx !== -1) {
    entries[idx].expires = Date.now() + TTL;
  } else {
    entries.push({ viewerId, expires: Date.now() + TTL });
  }
};

/** Check if `viewerId` is a recent viewer (non-destructive). */
export const hasRecentViewer = (viewerId: number): boolean => {
  cleanup();
  return entries.some(e => e.viewerId === viewerId);
};

/**
 * If `viewerId` is a recent viewer, remove the entry and return true.
 * Returns false if not found. This is a one-shot consume so the delay
 * only applies to the first message from that viewer.
 */
export const consumeRecentViewer = (viewerId: number): boolean => {
  cleanup();
  const idx = entries.findIndex(e => e.viewerId === viewerId);
  if (idx === -1) return false;
  entries.splice(idx, 1);
  return true;
};

/** Remove expired entries. */
const cleanup = (): void => {
  const now = Date.now();
  for (let i = entries.length - 1; i >= 0; i--) {
    if (entries[i].expires <= now) {
      entries.splice(i, 1);
    }
  }
};
